import React, { Suspense, useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import { constant } from '@medflex/commons';
import ErrorBoundaryEmpty from '@medflex/components/stories/ErrorBoundaryEmpty';
import { USER_TYPES, SESSION_TYPES } from '@medflex/utils/constants';
import PhysicianNavBar, { UserPreview } from '@medflex/components/stories/PhysicianNavBar';
import useIsSmallScreen from '@medflex/hooks/useIsSmallScreen';
import useUserSession from '@medflex/hooks/useUserSession';
import { FEATURE_KEYS } from '@medflex/utils/subscriptionEnums';
import Badge, { KINDS as BADGE_KINDS } from '@medflex/components/stories/Badge';
import { useTranslation } from 'react-i18next';
import useChatContacts from '../hooks/useChatContacts';
import useNavBarContext from '../hooks/useNavBarContext';
import useModal from '../hooks/useModal';
import useInvitationRequests from '../hooks/useInvitationRequests';
import usePermissions from '../hooks/usePermissions';
import useUserProfile from '../hooks/useUserProfile';
import useAddressBookContacts from '../hooks/useAddressBookContacts';
import useSessionMeetings from '../hooks/useSessionMeetings';
import useSessionChats from '../hooks/useSessionChats';
import getUnreadChatsCount from '../../utils/getUnreadChatsCount';
import useFeatures from '../hooks/useFeatures';
import useSubscription from '../hooks/useSubscription';

// eslint-disable-next-line import/no-unresolved
const RemoteInboxCount = React.lazy(() => import('RemoteInbox/UnreadRequests'));

const NavBarModule = ({ className }) => {
  const { contacts } = useChatContacts();
  const { pairedPhysicians, orgaPhysicians, pairedPatients, staff } = useAddressBookContacts();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const { invitationRequests } = useInvitationRequests();
  const [unreadInvitationRequestCount, setUnreadInvitationRequestCount] = useState(0);
  const { hasPatientAccess, hasPhysicianAccess } = usePermissions();
  const { userProfile } = useUserProfile();
  const history = useHistory();
  const { getQueryString } = useModal();
  const { futureMeetings } = useSessionMeetings();
  const { chats } = useSessionChats();
  const { session: userSession } = useUserSession();
  const [pamFeature] = useFeatures([FEATURE_KEYS.PAM]);
  const { t } = useTranslation('messenger', { keyPrefix: 'NavBarModule' });
  const { plan: subscriptionPlan, email: subscriptionEmail, isBasicUser } = useSubscription();

  const isSmallScreen = useIsSmallScreen();

  const { toggle: toggleNavBar, isCollapsed, status: navBarStatus } = useNavBarContext();

  const userPreviewData = useMemo(() => {
    if (userProfile.attributes) {
      const {
        fullNameWithTitle: title,
        workplace,
        workArea1,
        organization = {},
        initials,
        avatar,
        areaOfActivity,
      } = userProfile.attributes;

      return {
        title,
        subtitle: workplace || organization.name,
        info: workArea1 || constant.areaOfActivity[areaOfActivity],
        initials,
        avatar,
      };
    }
    return {};
  }, [userProfile]);

  useEffect(() => {
    if (invitationRequests.length > 0) {
      setUnreadInvitationRequestCount(invitationRequests.filter(({ seen }) => !seen).length);
    } else setUnreadInvitationRequestCount(0);
  }, [invitationRequests]);

  useEffect(() => {
    if (contacts && chats) {
      setUnreadMessages(getUnreadChatsCount(contacts, chats));
    }
  }, [contacts, chats]);

  const subscriptionMemo = useMemo(() => {
    return {
      plan: subscriptionPlan,
      email: subscriptionEmail,
      ...pamFeature,
    };
  }, [pamFeature, subscriptionEmail, subscriptionPlan]);

  return (
    navBarStatus === 'loaded' && (
      <PhysicianNavBar
        className={className}
        unreadMessages={unreadMessages}
        chatPath="/chat"
        onActionCallback={!isCollapsed && !isSmallScreen ? toggleNavBar : null}
        onClickOutside={toggleNavBar}
        numScheduledCalls={futureMeetings.length}
        isCollapsed={isCollapsed}
        invitationRequestsCount={hasPatientAccess ? invitationRequests.length : 0}
        unreadInvitationRequestCount={hasPatientAccess ? unreadInvitationRequestCount : 0}
        physiciansCount={pairedPhysicians.length + orgaPhysicians.length}
        patientsCount={pairedPatients.length}
        staffCount={staff.length}
        sessionChatCount={
          chats.filter(
            c => c.type !== SESSION_TYPES.INTERNAL1ON1 && c.type !== SESSION_TYPES.TEAM_CHAT,
          ).length
        }
        patientsShown={hasPatientAccess}
        physiciansShown={hasPhysicianAccess}
        invitationRequestsShown={hasPhysicianAccess || hasPatientAccess}
        userPreview={
          userProfile && userProfile.type === USER_TYPES.PHYSICIAN ? (
            <UserPreview {...userPreviewData} onClick={() => history.push('/settings/profile')} />
          ) : null
        }
        configShown={userProfile && userProfile.type === USER_TYPES.PHYSICIAN}
        getQueryString={getQueryString}
        inboxInMenuCollapsed={
          <Suspense fallback={null}>
            <ErrorBoundaryEmpty>
              <RemoteInboxCount
                jwt={userSession?.access_token}
                variant="indicator"
                subscription={subscriptionMemo}
              />
            </ErrorBoundaryEmpty>
          </Suspense>
        }
        inboxInMenuExpanded={
          pamFeature.active ? (
            <>
              <div className="flex-grow" />
              <div className="mx-4">
                <Suspense fallback={null}>
                  <ErrorBoundaryEmpty>
                    <RemoteInboxCount
                      jwt={userSession?.access_token}
                      variant="count"
                      subscription={subscriptionMemo}
                    />
                  </ErrorBoundaryEmpty>
                </Suspense>
              </div>
            </>
          ) : (
            isBasicUser && (
              <Badge kind={BADGE_KINDS.SIGNAL} className="ml-2" size="small">
                {t('upgrade')}
              </Badge>
            )
          )
        }
      />
    )
  );
};

NavBarModule.propTypes = {
  className: PropTypes.string,
};

export default NavBarModule;
